import React, { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Formik, Form } from 'formik'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import Card from '../../../../components/Card'
import { api, authHeaders } from '../../../../services/api'
import regex from '../../../../util/regex'
import masks from '../../../../util/masks'
import formUtils from '../../../../util/form'
import { validarCPF } from '../../../../util/validadores'
import { Button, Select, Textbox } from '../../../../components/Form'
import Table from '../../../../components/Table'
import Modal from '../../../../components/Modal'
import { FiArrowRight, FiArrowUp, FiPlus, FiTrash } from 'react-icons/fi'

const validation = Yup.object({
	name: Yup.string()
		.required('Nome obrigatório.'),
	email: Yup.string()
		.email('E-mail inválido.')
		.required('E-mail obrigatório.'),
	phone: Yup.string()
		.matches(regex.mobile, 'Celular inválido.')
		.required('O WhatsApp é obrigatório.'),
	cpf: Yup.string()
		.test('validar-cpf', 'CPF inválido.', value => validarCPF(value || ''))
		.required('O CPF é obrigatório.')
})

const initialValues = {
	name: '',
	email: '',
	phone: '',
	cpf: '',
	plano: null
}

export default function () {
	const [showFormAddGratuidade, setShowFormAddGratuidade] = useState(false)

	const { data: gratuidades, refetch: refetchGratuidades } = useQuery({
		queryKey: ['obter-gratuidades'],
		queryFn: async () => {
			const response = await api.get('usuario/gratuito', authHeaders())

			return response.map(item => ({
				id: item.id,
				nome: item.pessoa.nome,
				status: item.status,
				plano: `${item.modulo.descricao} ${item.plano.descricao}`
			}))
		},
		refetchOnWindowFocus: false
	})

	const { data: planos } = useQuery({
		queryKey: ['obter-planos-disponiveis-gratuidade'],
		queryFn: async () => {
			const response = await api.get('plano', {
				params: {
					modulo_id: 1
				}
			})

			return response
		},
		refetchOnWindowFocus: false
	})

	const { mutate: handleSubmit } = useMutation({
		mutationKey: ['confirm-user'],
		mutationFn: async data => {
			data = formUtils.extractFormValues(data)

			await api.post('usuario/gratuito', data, authHeaders())

			toast.success('Usuário criado com sucesso!')

			setShowFormAddGratuidade(false)

			refetchGratuidades()
		},
		onError: e => {
			toast.error(e.msg)
		}
	})

	const { mutate: handleToggleStatusAssinaturaGratuita } = useMutation({
		mutationFn: async data => {
			await api.put(`usuario/gratuito/${data.id}`, data, authHeaders())

			toast.success('A assinatura gratuita foi atualizada!')

			refetchGratuidades()
		},
		onError: e => {
			toast.error(e.msg)
		}
	})

	if (!planos || !gratuidades) {
		return null
	}

	const planosSelectOptions = planos
		.map(plano => ({
			label: `${plano.descricao} ${plano.periodo}`,
			value: plano.id
		}))

	return (
		<div className="flex flex-col text-slate-800 sm:mx-4 sm:my-6 md:mx-32 md:my-12 leading-loose">
			<Card className="flex flex-col">
				<h1 className="text-slate-800 mb-4">Cadastro de usuários gratuitos</h1>

				<Button className="self-end" onClick={() => {
					setShowFormAddGratuidade(true)
				}}>
					Cadastrar novo
					<FiPlus size={16} />
				</Button>

				<Table
					headers={[
						{ name: 'nome', value: 'Nome' },
						{ name: 'plano', value: 'Plano' }
					]}
					data={gratuidades}
					actions={item => {
						return item.status === 'authorized' ? [
							{
								name: 'Remover acesso',
								action: handleToggleStatusAssinaturaGratuita,
								icon: FiTrash,
								iconSize: 16
							}
						] : [
							{
								name: 'Retomar acesso',
								action: handleToggleStatusAssinaturaGratuita,
								icon: FiArrowUp,
								iconSize: 16
							}
						]
					}}
				/>

				<Modal
					isOpen={showFormAddGratuidade}
					handleClose={() => {
						setShowFormAddGratuidade(false)
					}}
					title="Cadastrar usuário gratuito"
				>
					<Formik
						initialValues={initialValues}
						validationSchema={validation}
						onSubmit={handleSubmit}
					>
						{({ setFieldValue, isSubmitting }) => (
							<Form className="flex flex-col md:grid md:grid-cols-3">
								<Textbox name="name" label="Nome" />

								<Textbox name="email" label="E-mail" />

								<Textbox name="phone" label="WhatsApp" mask={masks.mobile} />

								<Textbox name="cpf" label="CPF" mask={masks.cpf} />

								<Select
									name="plano"
									label="Escolha o plano"
									onChange={(selected, meta) => {
										setFieldValue(meta.name, selected)
									}}
									options={planosSelectOptions}
								/>

								<div className="flex justify-end md:col-span-3 mt-4">
									<Button type="submit" loading={isSubmitting}>
										Criar conta gratuita
										<FiArrowRight size={16} />
									</Button>
								</div>
							</Form>
						)}
					</Formik>
				</Modal>
			</Card>
		</div>
	)
}
